import React, { useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { updateBreadcrumb } from '#action/breadcrumb';
import { fetchDevicesLastLocations, clearDevicesLastLocations } from '#action/device/devices-last-locations';
import { clear } from '#action/device/devices-paginated';
import { fetchMeta } from '#action/meta/device';
import FilterWrapper from '#component/general/list-filter/filter-wrapper';
import { isFilterExist } from '#component/general/list-filter/helper';
import Map from './mapbox';
import {
  MapWrap,
  ContentContainer,
  MobileFilterContainer,
  FilterButtonContainer,
  SwitcherContainer
} from './styles-map';
import filterConfig from './filter/device-map-config';
import ViewSwitcher from './view-switcher';
import { setDocumentTitle } from '#helper';
import { useError } from '#hooks';

/**
 * Renders device dashboard map.
 * @returns {React.FC}
 */

const DashboardMap = props => {
  const dispatch = useDispatch();
  const meta = useSelector(s => s.meta, shallowEqual);
  const account = useSelector(s => s.account, shallowEqual);
  const {
    error: filterError
  } = useSelector(s => s.devicesFilter, shallowEqual);
  const { devices, status, error } = useSelector(s => s.devicesLastLocations, shallowEqual);
  const [loading, setLoading] = useState(true);
  const { history } = props;
  const theme = useTheme();
  const listFilter = useSelector(s => s.listFilter, shallowEqual);
  const filter = listFilter?.device;
  const filterExist = isFilterExist(filter);
  const filterLoaded = listFilter.status === 'succeeded';
  const filterData = useMemo(() => filterConfig, []);
  const isDevicesLoading = ['loading', 'idle'].includes(status) || !filterLoaded;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useError(error, filterError);

  useEffect(() => {
    setDocumentTitle('Devices overview');
    dispatch(updateBreadcrumb([]));
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchMeta());
      await dispatch(fetchDevicesLastLocations());
      setLoading(false);
    };

    if (filterLoaded) {
      fetchData();
    }

    return () => {
      dispatch(clear());
      dispatch(clearDevicesLastLocations());
    };
  }, [dispatch, filterLoaded]);

  return (
    <MapWrap>
      {isMobile ?
        <MobileFilterContainer>
          <FilterWrapper data={filterData} />
        </MobileFilterContainer> :
        <FilterButtonContainer>
          <FilterWrapper data={filterData} />
          <SwitcherContainer>
            <ViewSwitcher tab={1} />
          </SwitcherContainer>
        </FilterButtonContainer>}
      <ContentContainer>
        <Map
          devices={devices}
          history={history}
          account={account}
          loading={loading}
          isDevicesLoading={isDevicesLoading}
          meta={meta}
          filter={filterExist ? filter : null}
        />
      </ContentContainer>
    </MapWrap>
  );
};

export default DashboardMap;
